import type {
  Address,
  AutoPayEnrollment,
  ContactType,
  Invoice,
  Meter,
  MeterReading,
  Nudge,
  NudgePreferences,
  NudgeUtilityPreferences,
  // Organization,
  Payment,
  PaymentMethod,
  ServiceAccount,
  User,
  UserPreferences,
  UtilityServiceAccount,
} from "@tillix/db";
import type { SnapshotInOf } from "mobx-keystone";
import { dateToIsoString } from "./transformers";
import type { InvoiceModel } from "../mobx/core/invoice/model.invoice";
import type { MeterModel } from "../mobx/core/meter/model.meter";
import type { MeterReadingModel } from "../mobx/core/meter/model.meter-reading";
import type {
  NudgeModel,
  NudgePreferencesModel,
  NudgeUtilityPreferencesModel,
} from "../mobx/core/nudge/model.nudge";
import type {
  AutoPayEnrollmentModel,
  PaymentMethodModel,
  PaymentModel,
} from "../mobx/core/payment/model.payment";
import type { ServiceAccountModel } from "../mobx/core/service-account/model.service-account";
import type UserModel from "../mobx/core/user/model.user";
import type { UserContactModel } from "../mobx/core/user/model.user";

export function userDbToSnapshot(
  db: Omit<User, "id"> & {
    preferences?: Pick<
      UserPreferences,
      "language" | "communicationLanguage" | "theme"
    > | null;
    primaryAddress?: {
      street1: string;
      street2: string | null;
      city: string;
      state: string;
      zip: string;
      country: string;
      countryCodeId?: string | null;
    } | null;
  },
): SnapshotInOf<UserModel> {
  return {
    email: db.email,
    name: db.name ?? null,
    profileImage: db.profileImage ?? null,
    phoneNumber: db.phoneNumber ?? null,
    primaryAddress: db.primaryAddress
      ? {
          street1: db.primaryAddress.street1,
          street2: db.primaryAddress.street2 ?? null,
          city: db.primaryAddress.city,
          state: db.primaryAddress.state,
          country: db.primaryAddress.country,
          zip: db.primaryAddress.zip,
          countryCodeId: db.primaryAddress.countryCodeId ?? null,
        }
      : null,

    createdAt:
      typeof db.createdAt === "string"
        ? db.createdAt
        : dateToIsoString(db.createdAt),
    updatedAt:
      typeof db.updatedAt === "string"
        ? db.updatedAt
        : dateToIsoString(db.updatedAt ?? new Date()),
    lastSync: dateToIsoString(new Date()),
    ...(db.preferences && {
      preferences: {
        language: db.preferences.language,
        communicationLanguage: db.preferences.communicationLanguage,
        theme: db.preferences.theme,
      },
    }),
  };
}

export function sessionUserToUserModelSnapshot(sessionUser: {
  email: string;
  name?: string | null;
  phoneNumber?: string | null;
}): SnapshotInOf<UserModel> {
  const now = dateToIsoString(new Date());
  return {
    email: sessionUser.email,
    profileImage: null,
    name: sessionUser.name ?? null,
    phoneNumber: sessionUser.phoneNumber ?? null,
    primaryAddress: null,
    createdAt: now,
    updatedAt: now,
    lastSync: now,
  };
}

export function invoiceDbToSnapshot(
  db: Omit<Invoice, "meta"> & { meta: unknown },
): SnapshotInOf<InvoiceModel> {
  const meta = (db.meta ?? {}) as Record<string, unknown>;
  return {
    id: db.id,
    currency: db.currency as SnapshotInOf<InvoiceModel>["currency"],
    externalId: db.externalId,
    amount: db.amount,
    amountOpen: db.amountOpen,
    pdfURL: db.pdfURL,
    note: db.note,
    serviceAccountId: db.serviceAccountId,
    latePaymentAmount: Number(meta.latePaymentAmount) || 0,
    dueAt: dateToIsoString(db.dueAt),
    invoicedAt: dateToIsoString(db.invoicedAt),

    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
  };
}

export function serviceAccountDbToSnapshot(
  db: Omit<ServiceAccount, "meta"> & {
    meta: unknown;
    utilityServiceAccount:
      | (UtilityServiceAccount & {
          address: Omit<Address, "updatedAt" | "createdAt">;
        })
      | null;
  },
): SnapshotInOf<ServiceAccountModel> {
  return {
    id: db.id,
    type: db.type as SnapshotInOf<ServiceAccountModel>["type"],
    nameOnAccount: db.nameOnAccount,
    nickname: db.nickname,
    description: db.description,
    externalId: db.externalId,
    balance: db.balance,
    tenantId: db.tenantId,
    meta: db.meta as Record<string, unknown>,
    syncStatus: (db as any).syncStatus ?? "PENDING",
    syncFailureReason: (db as any).syncFailureReason ?? null,
    utilityServiceAccount: db.utilityServiceAccount
      ? {
          ...db.utilityServiceAccount,
          address: {
            street1: db.utilityServiceAccount.address.street1,
            street2: db.utilityServiceAccount.address.street2,
            city: db.utilityServiceAccount.address.city,
            state: db.utilityServiceAccount.address.state,
            zip: db.utilityServiceAccount.address.zip,
            country: db.utilityServiceAccount.address.country,
            countryCodeId: db.utilityServiceAccount.address.countryCodeId,
          },
          updatedAt: dateToIsoString(
            db.utilityServiceAccount?.updatedAt ?? new Date(),
          ),
        }
      : null,

    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
    dueAt: db.dueAt ? dateToIsoString(db.dueAt) : null,
  };
}

export function userContactDbToSnapshot(db: {
  id: string;
  type: ContactType;
  contactInfo: string;
  isPreferred: boolean;
  verified: boolean;
  serviceAccountId?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
}): SnapshotInOf<UserContactModel> {
  return {
    id: db.id,
    type: db.type,
    contactInfo: db.contactInfo,
    isPreferred: db.isPreferred,
    verified: db.verified,
    serviceAccountId: db.serviceAccountId ?? null,
    createdAt: dateToIsoString(db.createdAt ?? new Date()),
    updatedAt: dateToIsoString(db.updatedAt ?? new Date()),
  };
}

// Payment
export function paymentMethodDbToSnapshot(
  db: Omit<PaymentMethod, "meta"> & { meta: unknown },
): SnapshotInOf<PaymentMethodModel> {
  return {
    id: db.id,
    nickname: db.nickname,
    type: db.type,
    disabledReason: db.disabledReason,
    imageId: db.imageId,
    secondaryInfo: db.secondaryInfo,
    meta: db.meta as Record<string, unknown>,
    lastUsedAt: db.lastUsedAt !== null ? dateToIsoString(db.lastUsedAt) : null,

    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
  };
}

export function paymentDbToSnapshot(
  db: Omit<Payment, "meta"> & { meta: unknown },
): SnapshotInOf<PaymentModel> {
  return {
    id: db.id,
    confirmationNumber: db.confirmationNumber,
    discount: db.discount,
    fee: db.fee,
    paidAt: dateToIsoString(db.paidAt),
    amount: db.amount,
    status: db.status,
    description: db.description,
    serviceAccountId: db.serviceAccountId,
    paymentMethodId: db.paymentMethodId,
    meta: db.meta as Record<string, unknown>,

    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
  };
}

export function autoPayEnrollmentDbToSnapshot(
  db: AutoPayEnrollment,
): SnapshotInOf<AutoPayEnrollmentModel> {
  return {
    id: db.id,
    serviceAccountId: db.serviceAccountId,
    paymentMethodId: db.paymentMethodId,

    // step 1 values
    frequency: db.frequency,
    customFrequency: db.customFrequency,
    customFrequencyDay: db.customFrequencyDay,
    maxPaymentAmountPreference: db.maxPaymentAmountPreference,
    maxPaymentAmount: db.maxPaymentAmount,

    // step 2 values
    notificationPreferences:
      db.notificationPreferences as SnapshotInOf<AutoPayEnrollmentModel>["notificationPreferences"],

    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
  };
}

// Notifications
export function nudgeDbToSnapshot(
  db: Omit<Nudge, "data">,
): SnapshotInOf<NudgeModel> {
  return {
    id: db.id,
    type: db.type,
    message: db.message,
    read: db.read,
    notificationLogId: db.notificationLogId,
    source: db.source,
    serviceAccountId: db.serviceAccountId,
    paymentId: db.paymentId,
    channel: db.channel,

    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
  };
}

export function nudgePreferencesDbToSnapshot(
  db: NudgePreferences,
): SnapshotInOf<NudgePreferencesModel> {
  return {
    serviceAccountId: db.serviceAccountId,
    newBillAvailable: db.newBillAvailable,
    newBillEnabled: db.newBillEnabled,
    newBillChannels:
      db.newBillChannels as SnapshotInOf<NudgePreferencesModel>["newBillChannels"],
    paymentDueAvailable: db.paymentDueAvailable,
    paymentDueEnabled: db.paymentDueEnabled,
    paymentDueChannels:
      db.paymentDueChannels as SnapshotInOf<NudgePreferencesModel>["paymentDueChannels"],
    paymentReceivedAvailable: db.paymentReceivedAvailable,
    paymentReceivedEnabled: db.paymentReceivedEnabled,
    paymentReceivedChannels:
      db.paymentReceivedChannels as SnapshotInOf<NudgePreferencesModel>["paymentReceivedChannels"],
    paymentOverdueAvailable: db.paymentOverdueAvailable,
    paymentOverdueEnabled: db.paymentOverdueEnabled,
    paymentOverdueChannels:
      db.paymentOverdueChannels as SnapshotInOf<NudgePreferencesModel>["paymentOverdueChannels"],

    updatedAt: dateToIsoString(db.updatedAt),
  };
}

export function nudgeUtilityPreferencesDbToSnapshot(
  db: NudgeUtilityPreferences,
): SnapshotInOf<NudgeUtilityPreferencesModel> {
  return {
    serviceAccountId: db.serviceAccountId,
    newMeterReadAvailable: db.newMeterReadAvailable,
    newMeterReadEnabled: db.newMeterReadEnabled,
    newMeterReadChannels:
      db.newMeterReadChannels as SnapshotInOf<NudgeUtilityPreferencesModel>["newMeterReadChannels"],
    outageAlertAvailable: db.outageAlertAvailable,
    outageAlertEnabled: db.outageAlertEnabled,
    outageAlertChannels:
      db.outageAlertChannels as SnapshotInOf<NudgeUtilityPreferencesModel>["outageAlertChannels"],

    updatedAt: dateToIsoString(db.updatedAt),
  };
}

// TODO: come back to this once org db model is complete
// export function organizationDbToSnapshot(
//   db: Organization,
// ): SnapshotInOf<OrganizationModel> {
//   return {
//     name: db.name,
//     lastSync: dateToIsoString(new Date()),
//     tenantId: db.tenantId,
//     meta: (db.meta ?? {}) as Record<string, unknown>,
//     status: db.status as SnapshotInOf<OrganizationModel>["status"],
//     displayName: db.displayName,
//     description: db.description,
//     image: db.image,
//     email: db.email,
//     phone: db.phone,
//     createdAt: dateToIsoString(db.createdAt),
//     updatedAt: dateToIsoString(db.updatedAt),
//   };
// }

export function meterDbToSnapshot(db: Meter): SnapshotInOf<MeterModel> {
  return {
    id: db.id,
    meterNum: db.meterNum,
    serviceAddress: db.serviceAddress,
    location: db.location,
    status: db.status,
    serviceAccountId: db.serviceAccountId,
    lastMeasuredAt: db.lastMeasuredAt
      ? dateToIsoString(db.lastMeasuredAt)
      : null,
    nextMeasureAt: db.nextMeasureAt ? dateToIsoString(db.nextMeasureAt) : null,
    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
  };
}

export function meterReadingDbToSnapshot(
  db: MeterReading,
): SnapshotInOf<MeterReadingModel> {
  return {
    id: db.id,
    consumption: db.consumption,
    daysInCycle: db.daysInCycle,
    measuredAt: dateToIsoString(db.measuredAt),
    createdAt: dateToIsoString(db.createdAt),
    updatedAt: dateToIsoString(db.updatedAt),
    unitOfMeasure: db.unitOfMeasure,
    cost: db.cost,
    registerNum: db.registerNum,
    runningReading: db.runningReading,
    meterId: db.meterId,
  };
}
